import { useState, useEffect, useCallback } from "react";
import { CheckCircle2, Circle, Eye } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";
import { HoverCard, HoverCardTrigger, HoverCardContent } from "@/components/ui/hover-card";
import DocumentDetailDialog from "@/components/documents/DocumentDetailDialog";
import MarkDoneDialog, { doneNoteText } from "@/components/documents/MarkDoneDialog";
import SetupCard from "@/components/home/SetupCard";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { isDemoUser } from "@/lib/demo";
import { formatDate } from "@/components/documents/DocumentStats";
import { BRAND } from "@/lib/constants";

const EM_DASH = "—";

// Copied verbatim from DashboardStats.jsx / FundDetailPage.jsx / EventsTable.jsx —
// no shared currency-formatter lib exists in this codebase (per recon). Used for
// the per-row Amount column in the feed.
function formatCurrency(amount, currency = "USD") {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

const SECTION_HEADING_CLASS = "text-[11px] font-semibold uppercase tracking-wider text-gray-400 pl-2";
const SECTION_HEADING_STYLE = { borderLeft: `3px solid ${BRAND.bg}` };

function SectionHeading({ children }) {
  return (
    <h2 className={SECTION_HEADING_CLASS} style={SECTION_HEADING_STYLE}>
      {children}
    </h2>
  );
}

// The per-portal split behind one inflow tile. Percentages are derived HERE from the rows'
// own sum rather than sent by the backend, so the breakdown can never disagree with itself;
// that sum can legitimately be below the tile value (a doc with no scraped_at is counted in
// the tile but cannot be attributed to a window), which is why the footer states the total
// it actually split rather than echoing the tile.
function PortalBreakdown({ rows }) {
  if (!rows || rows.length === 0) {
    return <p className="text-xs text-gray-400">No documents in this window.</p>;
  }
  const total = rows.reduce((acc, r) => acc + (r.count || 0), 0);
  return (
    <div data-testid="portal-breakdown">
      <p className="text-[10px] font-medium text-gray-400 uppercase tracking-wider mb-2">
        By portal
      </p>
      <div className="max-h-56 overflow-y-auto space-y-1.5 pr-1">
        {rows.map((r) => {
          const pct = total > 0 ? (r.count / total) * 100 : 0;
          return (
            <div key={r.portal || "(unknown)"} className="text-xs">
              <div className="flex items-baseline justify-between gap-3">
                <span className="capitalize text-gray-700 truncate">{r.portal || "Unknown"}</span>
                <span className="font-mono text-gray-500 shrink-0 tabular-nums">
                  {r.count} · {pct.toFixed(0)}%
                </span>
              </div>
              <div className="mt-0.5 h-1 rounded-full bg-gray-100 overflow-hidden">
                <div
                  className="h-full rounded-full"
                  style={{ width: `${pct}%`, backgroundColor: BRAND.bg }}
                />
              </div>
            </div>
          );
        })}
      </div>
      <p className="mt-2 pt-2 border-t border-gray-100 text-[10px] text-gray-400">
        {total.toLocaleString()} document{total === 1 ? "" : "s"} across {rows.length} portal
        {rows.length === 1 ? "" : "s"}
      </p>
    </div>
  );
}

function Tile({ label, value, testId, breakdown }) {
  const hasBreakdown = Array.isArray(breakdown) && breakdown.length > 0;
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <p className="text-[10px] font-medium text-gray-400 uppercase tracking-wider mb-1">{label}</p>
          {hasBreakdown && (
            <HoverCard openDelay={120} closeDelay={100}>
              <HoverCardTrigger asChild>
                <button
                  type="button"
                  aria-label={`${label} — breakdown by portal`}
                  data-testid={`${testId}-breakdown-trigger`}
                  className="flex h-4 w-4 items-center justify-center rounded-full border border-gray-300 text-[9px] font-semibold text-gray-400 transition-colors hover:border-gray-400 hover:text-gray-600 focus:outline-none focus-visible:ring-1 focus-visible:ring-gray-400"
                >
                  %
                </button>
              </HoverCardTrigger>
              <HoverCardContent align="end" className="w-72 bg-white">
                <PortalBreakdown rows={breakdown} />
              </HoverCardContent>
            </HoverCard>
          )}
        </div>
        <p className="text-xl font-bold text-gray-900 font-mono" data-testid={testId}>
          {value}
        </p>
      </CardContent>
    </Card>
  );
}

// The Status column's control — same look and behavior as the Documents page's
// per-row Done toggle (DocumentTable.jsx): a ghost icon button flipping between
// a hollow gray Circle and a filled green CheckCircle2, with a matching tooltip.
// A row with no blob_path (nothing to write through to) renders disabled, not
// broken — there is no document identity to toggle.
function DoneToggle({ blobPath, isDone, onToggle, noteText = "" }) {
  const disabled = !blobPath;
  const label = disabled ? "Done unavailable" : isDone ? "Mark as not done" : "Mark as done";
  return (
    <>
    {isDone && noteText && (
      <span id={`done-note-${blobPath}`} className="sr-only">{noteText}</span>
    )}
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 w-7 p-0"
          data-testid="doc-done-toggle"
          data-blob-path={blobPath || ""}
          disabled={disabled}
          aria-label={label}
          aria-describedby={isDone && noteText ? `done-note-${blobPath}` : undefined}
          onClick={(e) => {
            e.stopPropagation();
            if (disabled) return;
            onToggle(blobPath, !isDone);
          }}
        >
          {isDone ? (
            <CheckCircle2 className="w-3.5 h-3.5 text-green-600" />
          ) : (
            <Circle className="w-3.5 h-3.5 text-gray-400" />
          )}
        </Button>
      </TooltipTrigger>
      <TooltipContent side="top">
        {label}
        {isDone && noteText && (
          <span data-testid="done-note" className="block max-w-[320px] whitespace-normal">
            {noteText}
          </span>
        )}
      </TooltipContent>
    </Tooltip>
    </>
  );
}

// Calls red, distributions green — the founder's colour contract. Each kind has its own box now,
// so the colour lives on the Amount rather than on a per-row chip.
const KIND_STYLE = {
  call: { label: "Call", bg: "#FDECEC", fg: "#A3161F", bar: BRAND.bg },
  distribution: { label: "Dist", bg: "#E8F5EC", fg: "#1E6B3A", bar: "#2E8B4F" },
};

// Founder call: 5 latest per box, the rest behind an in-place "Show more".
const CARD_PAGE = 5;

// The feed is read-only here — edits happen on the row itself (Done toggle),
// never on the source-doc panel. Same contract as DashboardPage's timeline
// sidebar (handleReadOnlyEdit): toast, then throw so EditableCell reverts.
async function handleReadOnlyEdit() {
  toast.info("The source document is read-only here — edit it from My Documents.");
  throw new Error("read-only");
}

// "+$200" / "−$200" against the previous NAV; "—" when there is nothing to compare to.
function NavChange({ nav, previous, currency }) {
  if (previous == null || nav == null) {
    return (
      <span className="text-[11px] text-gray-300" data-testid="nav-change">
        {EM_DASH}
      </span>
    );
  }
  const delta = nav - previous;
  const ccy = currency || "USD";
  if (delta === 0) {
    return (
      <span className="text-[11px] font-mono text-gray-400" data-testid="nav-change">
        {formatCurrency(0, ccy)}
      </span>
    );
  }
  const up = delta > 0;
  return (
    <span
      className="text-[11px] font-mono"
      style={{ color: up ? KIND_STYLE.distribution.fg : KIND_STYLE.call.fg }}
      data-testid="nav-change"
      data-direction={up ? "up" : "down"}
    >
      {up ? "+" : "−"}
      {formatCurrency(Math.abs(delta), ccy)}
    </span>
  );
}

// One box: title, up to CARD_PAGE rows, and a Show more / Show less button that expands THIS
// card in place (per-card state) from the rows the payload already carries.
function FeedCard({ title, caption, testId, rows, emptyLabel, renderRow, onViewDoc, onToggleDone }) {
  const [expanded, setExpanded] = useState(false);
  const all = rows || [];
  const shown = expanded ? all : all.slice(0, CARD_PAGE);
  return (
    <Card data-testid={testId}>
      <CardContent className="p-4">
        <div className="flex items-baseline justify-between mb-2">
          <SectionHeading>{title}</SectionHeading>
          {caption && <span className="text-[10px] text-gray-400">{caption}</span>}
        </div>
        {all.length === 0 ? (
          <p className="py-6 text-center text-xs text-gray-400">{emptyLabel}</p>
        ) : (
          <ul className="divide-y divide-gray-100">
            {shown.map((row, i) => (
              <li
                key={`${row.blob_path || ""}-${i}`}
                className="flex items-center gap-2 py-2"
                data-testid="feed-row"
              >
                <div className="min-w-0 flex-1">
                  <p className="text-[13px] text-gray-700 truncate">
                    {/* uppercase is CSS-only (Tailwind) -- the underlying row.fund value is
                        never mutated, so keys/matching elsewhere stay untouched. */}
                    <span className="uppercase">{row.fund || EM_DASH}</span>
                    {(row.excluded || row.discarded) && (
                      <span className="ml-2 inline-block rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-gray-500">
                        {row.discarded ? "Discarded" : "Excluded"}
                      </span>
                    )}
                  </p>
                  <p className="text-[11px] text-gray-500 truncate">{row.client || EM_DASH}</p>
                </div>
                <div className="text-right shrink-0">{renderRow(row)}</div>
                <div className="inline-flex items-center gap-0.5 shrink-0">
                  {row.blob_path && (
                    <button
                      type="button"
                      data-testid="event-view-doc"
                      data-blob-path={row.blob_path}
                      onClick={(e) => {
                        e.stopPropagation();
                        onViewDoc({
                          blob_name: row.blob_path,
                          original_filename: row.blob_path.split("/").pop(),
                        });
                      }}
                      className="flex-shrink-0 p-1 rounded hover:bg-gray-200 text-gray-400 hover:text-gray-700 focus:outline-none focus:ring-1 focus:ring-gray-300"
                      title="View source PDF"
                      aria-label="View source PDF"
                    >
                      <Eye className="w-3.5 h-3.5" />
                    </button>
                  )}
                  <DoneToggle blobPath={row.blob_path} isDone={!!row.is_done} onToggle={onToggleDone} noteText={doneNoteText(row)} />
                </div>
              </li>
            ))}
          </ul>
        )}
        {all.length > CARD_PAGE && (
          <div className="mt-2 text-center">
            <Button
              variant="ghost"
              size="sm"
              className="text-xs text-gray-500 hover:text-gray-900"
              data-testid={`${testId}-toggle`}
              onClick={() => setExpanded((v) => !v)}
            >
              {expanded ? "Show less" : "Show more"}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function EventRowValue({ row }) {
  return (
    <>
      <p
        className="text-[13px] font-mono font-medium"
        style={row.amount != null && KIND_STYLE[row.kind] ? { color: KIND_STYLE[row.kind].fg } : undefined}
      >
        {row.amount != null ? formatCurrency(row.amount, row.currency || "USD") : EM_DASH}
      </p>
      <p className="text-[11px] text-gray-500">{row.received ? formatDate(row.received) : EM_DASH}</p>
    </>
  );
}

function NavRowValue({ row }) {
  return (
    <>
      <p className="text-[13px] font-mono font-medium text-gray-900">
        {row.nav != null ? formatCurrency(row.nav, row.currency || "USD") : EM_DASH}
      </p>
      <NavChange nav={row.nav} previous={row.previous} currency={row.currency} />
      <p className="text-[11px] text-gray-500">{row.as_of ? formatDate(row.as_of) : EM_DASH}</p>
    </>
  );
}

function HomeHeader({ subtitle }) {
  return (
    <div className="flex items-center justify-between">
      <div>
        <h1 className="text-xl font-bold text-gray-900">Elementar.</h1>
        <p className="text-xs text-gray-500 mt-0.5">Back office operations — fund cashflow tracking</p>
      </div>
      {subtitle}
    </div>
  );
}

export default function HomePage() {
  const authUser = useAuth()?.user;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [now, setNow] = useState(() => new Date());

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get("/home/summary");
      setData(res.data);
    } catch {
      setError("Failed to load home summary");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  // Live clock in the header — purely a client-side "as of" readout, no data
  // dependency, ticks on its own.
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(id);
  }, []);

  // Functional updates only — no stale-closure risk on either the optimistic
  // flip or its rollback. A blob_path may legitimately repeat across rows (a
  // single document can carry more than one line), so every matching row in
  // the feed flips together, same as the underlying Cosmos record they all
  // point at. This MUST write `events` -- it wrote the pre-merge
  // capital_calls/distributions keys for one release, which the payload has not
  // carried since the feed was merged, so the flip and its rollback were both
  // silent no-ops and the checkmark only moved on the next poll.
  const flipDone = useCallback((blobPath, val, note) => {
    const patch = val
      ? { is_done: true, done_note: note ?? null, done_at: note ? new Date().toISOString() : null }
      : { is_done: false, done_note: null, done_by: null, done_at: null };
    setData((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        events: (prev.events || []).map((r) => (r.blob_path === blobPath ? { ...r, ...patch } : r)),
        nav_updates: (prev.nav_updates || []).map((r) => (r.blob_path === blobPath ? { ...r, ...patch } : r)),
      };
    });
  }, []);

  // Same optimistic-update-then-rollback-on-error pattern as DocumentsPage.jsx's
  // handleMarkDoneSingle/handleUndoneSingle, wired to the same existing
  // PATCH /api/documents/{blob_name}/done endpoint — no new backend route.
  const handleToggleDone = useCallback(
    async (blobPath, nextIsDone, note) => {
      if (!blobPath) return;
      flipDone(blobPath, nextIsDone, note);
      try {
        await api.patch(
          `/documents/${encodeURIComponent(blobPath)}/done`,
          nextIsDone ? { is_done: true, note } : { is_done: false }
        );
      } catch {
        flipDone(blobPath, !nextIsDone);
        toast.error(nextIsDone ? "Failed to mark as done" : "Failed to mark as not done");
      }
    },
    [flipDone]
  );

  // Confirm step: the row control only asks; the PATCH goes out on confirm.
  const [doneDialog, setDoneDialog] = useState(null);
  const requestToggleDone = useCallback((blobPath, nextIsDone) => {
    if (blobPath) setDoneDialog({ blobPath, nextIsDone });
  }, []);
  const confirmDone = (note) => {
    const d = doneDialog;
    setDoneDialog(null);
    if (d) handleToggleDone(d.blobPath, d.nextIsDone, note);
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <HomeHeader subtitle={<p className="text-xs text-gray-400">Loading...</p>} />
        <div className="grid grid-cols-3 gap-3">
          {[1, 2, 3].map((i) => (
            <Card key={i}>
              <CardContent className="p-4 space-y-2">
                <Skeleton className="h-3 w-16" />
                <Skeleton className="h-6 w-12" />
              </CardContent>
            </Card>
          ))}
        </div>
        {[1, 2, 3].map((i) => (
          <Card key={`skel-${i}`}>
            <CardContent className="p-4 space-y-3">
              {[1, 2, 3].map((j) => (
                <div key={j} className="flex items-center gap-4">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-4 w-20" />
                  <Skeleton className="h-4 w-16 ml-auto" />
                </div>
              ))}
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <HomeHeader />
        <div className="p-12 text-center">
          <p className="text-sm" style={{ color: BRAND.text }}>
            {error}
          </p>
          <button
            onClick={fetchSummary}
            className="mt-3 text-xs underline"
            style={{ color: BRAND.text }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const {
    docs_today,
    docs_week,
    docs_month,
    events_basis,
    docs_by_portal,
    events,
    nav_updates,
  } = data;

  // Self-describing — never hardcoded to one basis. An "occurred" date and a "received" date
  // are different facts and must not share one static label.
  const eventsLabel = events_basis === "occurred" ? "Capital events that occurred" : "Capital events received";

  // ponytail: the payload carries every row (per kind); each card's "Show more" just stops
  // slicing. No second request, no pagination state.
  const eventRows = events || [];
  const callRows = eventRows.filter((r) => r.kind === "call");
  const distRows = eventRows.filter((r) => r.kind === "distribution");
  const byPortal = docs_by_portal || {};

  return (
    // Radix Tooltip throws "must be used within TooltipProvider" and white-screens
    // the whole page if no Provider is an ancestor. There is no global one — every
    // page wraps its own (DocumentTable.jsx, CatalogPage.jsx, LeaderboardTab.jsx).
    // One provider at the page root covers both queue tables' Done toggles.
    <TooltipProvider delayDuration={200}>
    <div className="-m-6 lg:-m-8 p-6 lg:p-8 min-h-full space-y-6" style={{ backgroundColor: "#F7F6F3" }}>
      <HomeHeader
        subtitle={
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <span className="inline-block w-2 h-2 rounded-full bg-green-500" />
            {now.toLocaleString("en-US", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
          </div>
        }
      />

      {!isDemoUser(authUser) && <SetupCard />}

      <section>
        <SectionHeading>Docs processed</SectionHeading>
        <div className="grid grid-cols-3 gap-3 mt-2">
          <Tile label="Today" value={docs_today} testId="tile-docs-today" breakdown={byPortal.today} />
          <Tile label="This week" value={docs_week} testId="tile-docs-week" breakdown={byPortal.week} />
          <Tile label="This month" value={docs_month} testId="tile-docs-month" breakdown={byPortal.month} />
        </div>
      </section>

      <section>
        <p className="text-[10px] text-gray-400 mb-2">{eventsLabel}</p>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 items-start">
          <FeedCard
            title="Capital calls"
            testId="card-calls"
            rows={callRows}
            emptyLabel="No capital calls yet."
            renderRow={(row) => <EventRowValue row={row} />}
            onViewDoc={setSelectedDoc}
            onToggleDone={requestToggleDone}
          />
          <FeedCard
            title="Distributions"
            testId="card-distributions"
            rows={distRows}
            emptyLabel="No distributions yet."
            renderRow={(row) => <EventRowValue row={row} />}
            onViewDoc={setSelectedDoc}
            onToggleDone={requestToggleDone}
          />
          <FeedCard
            title="NAV updates"
            testId="card-nav"
            rows={nav_updates || []}
            emptyLabel="No NAV updates yet."
            renderRow={(row) => <NavRowValue row={row} />}
            onViewDoc={setSelectedDoc}
            onToggleDone={requestToggleDone}
          />
        </div>
      </section>
      <DocumentDetailDialog
        doc={selectedDoc}
        open={!!selectedDoc}
        onOpenChange={(open) => {
          if (!open) setSelectedDoc(null);
        }}
        onEditField={handleReadOnlyEdit}
      />
      <MarkDoneDialog
        open={!!doneDialog}
        mode={doneDialog?.nextIsDone ? "done" : "undone"}
        onConfirm={confirmDone}
        onCancel={() => setDoneDialog(null)}
      />
    </div>
    </TooltipProvider>
  );
}
